import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { LayoutDashboard, CalendarDays, Users, Gift, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import {
  OverviewTab,
  BookingsTab,
  ClientsTab,
  GiftCardsTab,
} from "@/components/admin/AdminTabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "bookings", label: "Bookings", icon: CalendarDays },
  { key: "clients", label: "Clients", icon: Users },
  { key: "gift-cards", label: "Gift Cards", icon: Gift },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [tab, setTab] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const doLogout = async () => {
    await logout();
    setLocation("/login");
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [b, g, a] = await Promise.all([
          axios.get(`${API}/bookings`),
          axios.get(`${API}/gift-cards`),
          axios.get(`${API}/analytics`),
        ]);
        setBookings(b.data || []);
        setGiftCards(g.data || []);
        setAnalytics(a.data || null);
      } catch (e) {
        toast.error("Couldn't load dashboard data. Please refresh or sign in again.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const uniqueClients = (() => {
    const map = new Map();
    bookings.forEach((b) => {
      if (!map.has(b.email)) map.set(b.email, b);
    });
    return Array.from(map.values());
  })();

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-spa-sand flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 bg-spa-green text-spa-cream flex-col">
        <div className="p-6 border-b border-spa-cream/10">
          <Link href="/">
            <div className="cursor-pointer">
              <Logo variant="light" size="sm" />
              <p className="text-[10px] uppercase tracking-[0.18em] text-spa-cream/60 mt-3">Admin Console</p>
            </div>
          </Link>
        </div>
        <nav className="p-4 flex-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`admin-nav-${n.key}`}
              onClick={() => setTab(n.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors mb-1 ${
                tab === n.key
                  ? "bg-spa-cream/10 text-spa-cream"
                  : "text-spa-cream/60 hover:text-spa-cream hover:bg-spa-cream/5"
              }`}
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-spa-cream/10">
          {user && typeof user === "object" && (
            <p data-testid="admin-user-email" className="text-xs text-spa-cream/50 px-4 mb-3 truncate">
              Signed in as {user.email}
            </p>
          )}
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            className="w-full text-xs text-spa-cream/70 hover:text-spa-cream flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-spa-cream/5 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
          </button>
          <Link href="/" data-testid="admin-back-to-site">
            <button className="w-full text-xs text-spa-cream/50 hover:text-spa-cream flex items-center gap-2 px-4 py-2 mt-1">
              ← Back to public site
            </button>
          </Link>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 sm:p-10">
        {/* Mobile nav */}
        <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto pb-2">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`admin-nav-mobile-${n.key}`}
              onClick={() => setTab(n.key)}
              className={`px-4 py-2 rounded-full text-xs whitespace-nowrap ${
                tab === n.key ? "bg-spa-green text-spa-cream" : "bg-white border border-spa-border text-spa-gray"
              }`}
            >
              {n.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-spa-gray">Loading dashboard…</p>
        ) : (
          <>
            {tab === "overview" && (
              <OverviewTab analytics={analytics} bookings={bookings} giftCards={giftCards} uniqueClients={uniqueClients} />
            )}
            {tab === "bookings" && <BookingsTab bookings={bookings} />}
            {tab === "clients" && <ClientsTab clients={uniqueClients} />}
            {tab === "gift-cards" && <GiftCardsTab giftCards={giftCards} />}
          </>
        )}
      </main>
    </div>
  );
}
